import { type ReactNode } from 'react';
import {
  CalendarIcon,
  PinIcon,
  type SportName,
  SPORT_REGISTRY,
} from '@bravemove/design-tokens/icons';
import { Avatar } from '../Avatar/Avatar';
import { cn } from '../../utils/cn';

export type ChatBubbleVariant = 'sent' | 'received' | 'system' | 'booking-link';

export interface ChatBubbleBookingInfo {
  readonly sport: SportName;
  readonly venueName: string;
  readonly startAt: string;
}

export interface ChatBubbleProps {
  readonly variant: ChatBubbleVariant;
  readonly children?: ReactNode;
  readonly senderName?: string;
  readonly senderAvatarSrc?: string;
  readonly timestamp?: string;
  readonly read?: boolean;
  readonly booking?: ChatBubbleBookingInfo;
  readonly onPressBooking?: () => void;
  readonly className?: string;
}

export function ChatBubble({
  variant,
  children,
  senderName,
  senderAvatarSrc,
  timestamp,
  read,
  booking,
  onPressBooking,
  className,
}: ChatBubbleProps) {
  if (variant === 'system') {
    return (
      <div
        className={cn(
          'mx-auto inline-flex items-center justify-center px-3 py-1 text-[11px] text-ink-3 bg-surface-2 rounded-full',
          'border border-hairline-2 font-mono uppercase tracking-wider',
          className,
        )}
        role="status"
      >
        {children}
      </div>
    );
  }

  const isSent = variant === 'sent';
  const align = isSent ? 'items-end' : 'items-start';

  if (variant === 'booking-link' && booking) {
    const SportIcon = SPORT_REGISTRY[booking.sport];
    return (
      <div className={cn('flex flex-col w-full', align, className)}>
        <button
          type="button"
          onClick={onPressBooking}
          className={cn(
            'max-w-[80%] flex items-start gap-2 p-2.5 rounded-xl border border-hairline-2 bg-surface text-start',
            'hover:bg-surface-2 transition-colors duration-[120ms]',
            'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25',
          )}
        >
          <div className="shrink-0 size-9 rounded-lg bg-accent-soft text-accent inline-flex items-center justify-center">
            <SportIcon size={22} color="currentColor" dot="currentColor" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-ink-3 font-mono uppercase tracking-wider">Booking</div>
            <div className="text-[13px] font-bold text-ink truncate capitalize">
              {booking.sport.replace(/-/g, ' ')}
            </div>
            <div className="text-[11.5px] text-ink-2 flex items-center gap-1 mt-0.5">
              <PinIcon size={11} color="currentColor" />
              <span className="truncate">{booking.venueName}</span>
            </div>
            <div className="text-[11.5px] text-ink-2 flex items-center gap-1 mt-0.5">
              <CalendarIcon size={11} color="currentColor" />
              <span className="font-mono tabular-nums">{booking.startAt}</span>
            </div>
          </div>
        </button>
        {timestamp ? (
          <span className="text-[10px] text-ink-3 font-mono mt-0.5">{timestamp}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn('flex w-full gap-2', isSent ? 'flex-row-reverse rtl:flex-row' : '', className)}
    >
      {!isSent && senderName ? (
        <Avatar name={senderName} src={senderAvatarSrc} size="sm" className="shrink-0" />
      ) : null}
      <div className={cn('flex flex-col max-w-[75%]', align)}>
        {!isSent && senderName ? (
          <div className="text-[11px] text-ink-3 font-semibold mb-0.5 px-1">{senderName}</div>
        ) : null}
        <div
          className={cn(
            'rounded-2xl px-3 py-2 text-[13.5px] leading-snug',
            isSent
              ? 'bg-accent text-accent-ink rounded-ee-sm rtl:rounded-ee-2xl rtl:rounded-es-sm'
              : 'bg-surface-2 text-ink rounded-es-sm rtl:rounded-es-2xl rtl:rounded-ee-sm',
          )}
        >
          {children}
        </div>
        {timestamp || (isSent && read !== undefined) ? (
          <div
            className={cn(
              'text-[10px] text-ink-3 font-mono mt-0.5 flex items-center gap-1',
              isSent ? 'flex-row-reverse rtl:flex-row' : '',
            )}
          >
            {timestamp}
            {isSent && read !== undefined ? (
              <span className={read ? 'text-accent' : 'text-ink-3'}>{read ? '✓✓' : '✓'}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
